import { getRoot, Instance, SnapshotOut, types } from "mobx-state-tree"
import {
  ContactData,
  ContactList,
  ContactUpdatedData,
  Event,
  EventNames,
  QueryOnlineData,
  ContactDeletedData,
  MessageStatus,
  MessageTypes,
} from "~proto"
import { Contact, ContactModel } from "../contact/contact"
import { withEnvironment } from "../extensions/with-environment"
import { NotifyType } from "../../types/message"
import { RootStoreModel } from "~models/root-store"

type TypeLetterListData = {
  letter: string
  data: string[]
}

function makeLetterListData<T extends Record<string, any>>(
  filter: (d: T) => boolean,
  data: T[],
  key: keyof T,
): TypeLetterListData[] {
  const dst = {} as Record<ValueOf<T>, T[]>
  Array.from(data)
    .filter(filter)
    .forEach((d: T) => {
      const v = d[key]
      dst[v] = dst[v] || []
      dst[v].push(d)
    })

  const lst = Object.keys(dst).reduce((acc, letter) => {
    if (dst[letter].length) acc.push({ letter, data: dst[letter] })
    return acc
  }, [] as TypeLetterListData[])

  return lst
}

/**
 * Model description here for TypeScript hints.
 */
export const ContactStoreModel = types
  .model("ContactStore")
  .props({
    contacts: types.optional(types.map(ContactModel), {}),
  })
  .extend(withEnvironment)
  .views((self) => {
    function usersByName() {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const dst = {}
      Array.from(self.contacts.values())
        .filter((contact: Contact) => contact.user.id !== userStore.currentUser.id)
        .forEach((contact: Contact) => {
          const user = contact.user
          const nameLetter = user.name?.substring(0, 1)
          dst[nameLetter] = dst[nameLetter] || []
          dst[nameLetter].push(user)
        })
      const lst = Object.keys(dst).map((letter) => ({ letter, data: dst[letter] }))
      return lst
    }
    function _contactNameDefaultFilter(filter: RegExp, contact: Contact) {
      return (
        filter.test(contact.user.username) ||
        filter.test(contact.user.name) ||
        filter.test(contact.name)
      )
    }
    function size() {
      return self.contacts.size
    }
    function getContactList() {
      return Array.from(self.contacts.values())
    }

    function getContactListByKeyword(keyword?: string | RegExp) {
      const data = Array.from(self.contacts.values())
      if (!keyword) return data
      const reg = keyword instanceof RegExp ? keyword : new RegExp(String(keyword).trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i")
      return data.filter((contact: Contact) => {
        return (
          reg.test(contact.user?.username || "") ||
          reg.test(contact.user?.name || "") ||
          reg.test(contact.name || "")
        )
      })
    }
    function byName() {
      const data = Array.from(self.contacts.values())
      return makeLetterListData<typeof data[0]>(() => true, data, "name_letter")
    }
    function byNameWithFilter(filter: RegExp) {
      const filterFunc = (contact: Contact) => {
        return _contactNameDefaultFilter(filter, contact)
      }
      const data = Array.from(self.contacts.values())
      return makeLetterListData<typeof data[0]>(filterFunc, data, "name_letter")
    }
    function byNameWithFilterAndExcludeIds(filter: RegExp, excludeIds: number[]) {
      const filterFunc = (contact: Contact) => {
        return !excludeIds.includes(contact.user_id) && _contactNameDefaultFilter(filter, contact)
      }
      const data = Array.from(self.contacts.values())
      return makeLetterListData<typeof data[0]>(filterFunc, data, "name_letter")
    }
    function byNameWithFilterAndIncludeIds(filter: RegExp, includeIds: number[]) {
      const filterFunc = (contact: Contact) => {
        return includeIds.includes(contact.user_id) && _contactNameDefaultFilter(filter, contact)
      }
      const data = Array.from(self.contacts.values())
      return makeLetterListData<typeof data[0]>(filterFunc, data, "name_letter")
    }
    function byUserId(id: number) {
      return Array.from(self.contacts.values()).find((x) => x.user.id === id)
    }
    function byUsername(username: string) {
      return Array.from(self.contacts.values()).find((x) => x.user.username === username)
    }
    // 根据用户id来显示名称
    function getDisplayName(user_id: number) {
      const contact = byUserId(user_id)
      if (contact) {
        return contact.displayName
      }
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const user = userStore.users.get(user_id)
      if (user) {
        return user.name
      }
    }
    return {
      get usersByName() {
        return usersByName()
      },
      get size() {
        return size()
      },
      get byName() {
        return byName()
      },
      byNameWithFilter,
      byNameWithFilterAndExcludeIds,
      byNameWithFilterAndIncludeIds,
      byUserId,
      byUsername,
      getDisplayName,
      getContactList,
      getContactListByKeyword
    }
  })
  .actions((self) => {
    function clear() {
      self.contacts.clear()
    }
    function addContact({ user, ...contact }: ContactData) {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const u = userStore.addUser(user)
      return self.contacts.put({ ...contact, user: u.id })
    }
    // event-store
    function onContactCreatedHandler({ user, ...contact }: ContactData) {
      const { chatStore, userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const prevContact = self.contacts.get(contact.id.toString())
      if (user.id === userStore.currentUser.id) return
      const currentContact = addContact({
        user,
        ...contact,
      })
      currentContact.setDeleted(prevContact ? prevContact.deleted : true)
      const chat = chatStore.newPrivateChat({
        receiver_id: currentContact.user.id,
        name: currentContact.displayName,
      })
      if (!prevContact) {
        chat.newMessage({
          content: "已加为好友",
          type: MessageTypes.TEXT,
          status: MessageStatus.READ,
          notify_type: NotifyType.FRIEND_ADDED_BUT_NOT_APPROVED,
        })
      }
      chat.setDeleted(false)
      return currentContact
    }
    function onContactCreated({ data }: Event<ContactData>) {
      onContactCreatedHandler(data)
    }
    function fetchOnline() {
      if (self.contacts.size > 0) {
        const evt = new Event<QueryOnlineData>(EventNames.QUERY_ONLINE, {})
        const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
        eventStore.publish(evt)
      }
    }
    // event-store
    function onContactList({ data: { items } }: Event<ContactList>) {
      items.forEach((data) => addContact(data))
    }
    // event-store
    function onContactUpdated({ data: { id, ...values } }: Event<ContactUpdatedData>) {
      const contact = self.contacts.get(id.toString()) // 根据ID获取需要更新资料的用户
      contact.update(values)
      return contact
    }
    // event-store
    function onContactDeleted({ data: { id, owner_id, user_id } }: Event<ContactDeletedData>) {
      const {
        userStore: { currentUser },
        chatStore,
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      const isSelf = currentUser.id === owner_id // 是否自己主動刪除
      if (isSelf) {
        self.contacts.delete(id.toString())
        const chat = chatStore.chats.get(user_id)
        chatStore.setCurrent(undefined)
        chat && chatStore.deletePrivateChat(user_id)
      } else {
        const targetContact = self.byUserId(owner_id)
        targetContact && targetContact.setStatus(0)
      }
    }
    return {
      clear,
      onContactCreated,
      onContactCreatedHandler,
      addContact,
      fetchOnline,
      onContactList,
      onContactUpdated,
      onContactDeleted,
    }
  })

type ContactStoreType = Instance<typeof ContactStoreModel>
export interface ContactStore extends ContactStoreType {}
type ContactStoreSnapshotType = SnapshotOut<typeof ContactStoreModel>
export interface ContactStoreSnapshot extends ContactStoreSnapshotType {}
export const createContactStoreDefaultModel = () => types.optional(ContactStoreModel, {})
